import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js';
import { FiDatabase, FiUsers, FiFileText, FiActivity, FiTrendingUp, FiEye } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const colors = ['#32966c','#e86f4c','#d4a012','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#f97316','#6366f1','#ef4444'];

const DashboardOverview = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        if (user?.role === 'admin') {
          const res = await api.get('/admin/dashboard');
          setStats(res.data.data);
        } else {
          const res = await api.get('/datasets/statistik');
          setStats(res.data.data);
        }
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    load();
  }, [user]);

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  const isAdmin = user?.role === 'admin';

  return (
    <div>
      <div className="dashboard-header">
        <h2>Selamat Datang, {user?.nama?.split(' ')[0]}! 👋</h2>
        <p>Ringkasan data portal NGADHU - {new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid" style={{ marginBottom: '2rem' }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'var(--primary-100)', color: 'var(--primary-600)' }}><FiDatabase /></div>
          <div>
            <div className="stat-value">{isAdmin ? stats?.totalDatasets : stats?.totalDataset}</div>
            <div className="stat-label">Total Dataset</div>
          </div>
        </div>
        {isAdmin && (
          <>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#dbeafe', color: '#1d4ed8' }}><FiUsers /></div>
              <div><div className="stat-value">{stats?.totalUsers}</div><div className="stat-label">Total User</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: 'var(--accent-100)', color: 'var(--accent-600)' }}><FiFileText /></div>
              <div><div className="stat-value">{stats?.totalBerita}</div><div className="stat-label">Total Berita</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: 'var(--gold-100)', color: 'var(--gold-600)' }}><FiActivity /></div>
              <div><div className="stat-value">{stats?.totalOrganisasi}</div><div className="stat-label">Organisasi</div></div>
            </div>
          </>
        )}
        {!isAdmin && (
          <>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#dbeafe', color: '#1d4ed8' }}><FiEye /></div>
              <div><div className="stat-value">{stats?.totalViews?.toLocaleString('id-ID') || 0}</div><div className="stat-label">Total Dilihat</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: 'var(--gold-100)', color: 'var(--gold-600)' }}><FiTrendingUp /></div>
              <div><div className="stat-value">{stats?.byKategori?.length || 0}</div><div className="stat-label">Kategori</div></div>
            </div>
          </>
        )}
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        {isAdmin && stats?.usersByRole && (
          <div className="chart-container">
            <h3>👥 User per Role</h3>
            <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Doughnut data={{
                labels: stats.usersByRole.map(r => r._id?.charAt(0).toUpperCase() + r._id?.slice(1)),
                datasets: [{ data: stats.usersByRole.map(r => r.count), backgroundColor: colors.slice(0, 3), borderWidth: 3, borderColor: '#fff' }]
              }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
            </div>
          </div>
        )}
        <div className="chart-container">
          <h3>📊 Dataset per Kategori</h3>
          <div style={{ height: 250 }}>
            <Bar data={{
              labels: (isAdmin ? stats?.datasetsByCategory || [] : stats?.byKategori || []).map(k => k._id?.charAt(0).toUpperCase() + k._id?.slice(1)),
              datasets: [{ data: (isAdmin ? stats?.datasetsByCategory || [] : stats?.byKategori || []).map(k => k.count), backgroundColor: colors, borderRadius: 6, borderSkipped: false }]
            }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, grid: { color: '#eaf0f5' } }, x: { grid: { display: false } } } }} />
          </div>
        </div>
      </div>

      {/* Recent Activity (Admin) */}
      {isAdmin && stats?.recentActivity?.length > 0 && (
        <div className="card">
          <div className="card-body">
            <h3 style={{ fontFamily: 'var(--font-body)', fontSize: '1.1rem', marginBottom: '1rem' }}>🕐 Aktivitas Terbaru</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {stats.recentActivity.slice(0, 10).map((act, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', borderBottom: i < 9 ? '1px solid var(--gray-100)' : 'none' }}>
                  <div className="user-avatar" style={{ width: 32, height: 32, fontSize: '0.7rem' }}>{act.user?.nama?.charAt(0) || '?'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.85rem' }}><strong>{act.user?.nama || 'Unknown'}</strong> — {act.details || act.action}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{new Date(act.createdAt).toLocaleString('id-ID')}</div>
                  </div>
                  <span className={`badge ${act.action?.includes('CREATE') ? 'badge-success' : act.action?.includes('DELETE') ? 'badge-danger' : 'badge-info'}`}>{act.action}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {(user?.role === 'admin' || user?.role === 'opd') && (
          <Link to="/dashboard/datasets" className="btn btn-primary"><FiDatabase size={16} /> Kelola Dataset</Link>
        )}
        {(user?.role === 'admin' || user?.role === 'opd') && (
          <Link to="/dashboard/berita" className="btn btn-secondary"><FiFileText size={16} /> Kelola Berita</Link>
        )}
        <Link to="/dataset" className="btn btn-ghost"><FiEye size={16} /> Lihat Portal</Link>
      </div>
    </div>
  );
};

export default DashboardOverview;
